import React, { useState } from 'react';
import { Box, Flex, Tooltip, Button } from '@radix-ui/themes';
import { CheckCircledIcon, PlusIcon } from '@radix-ui/react-icons';
import { GraphQLTable, Column } from '../../components/GraphQLTable';
import { AddressDisplay } from '../../components/AddressDisplay';
import ViewButton from '../../components/ViewButton';
import ImportAbiModal from '../../components/ImportAbiModal';
import InstrumentationChipGroup from '../../components/InstrumentationChip/InstrumentationChipGroup';
import {
  GetContractsDocument,
  GetContractsQuery,
  GetContractsQueryVariables,
} from '../../graphql/queries/GetContracts.generated';

type ContractNode = GetContractsQuery['contracts']['edges'][0]['node'];

const ContractList: React.FC = () => {
  const [importModalOpen, setImportModalOpen] = useState(false);

  const handleImportSuccess = () => {
    // Table will auto-refresh on next fetch
    console.log('ABI imported successfully');
  };

  const columns: Column<ContractNode>[] = [
    {
      header: 'Name',
      accessor: 'name',
      render: ({ value, row }) => (
        <Flex gap="2" align="center">
          {value || 'Unknown'}
          {row.abi && (
            <Tooltip content="Verified contract">
              <CheckCircledIcon
                color="green"
                style={{ width: '16px', height: '16px' }}
              />
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      header: 'Address',
      accessor: 'address',
      render: ({ value }) => <AddressDisplay address={value} />,
    },
    {
      header: 'Chain ID',
      accessor: 'chainId',
    },
    {
      header: 'Deploy Block',
      accessor: 'deploymentBlock',
      render: ({ value }) => value?.toLocaleString() ?? 'N/A',
    },
    {
      header: 'Deployer',
      accessor: 'deployer',
      render: ({ value }) =>
        value ? <AddressDisplay address={value} /> : 'N/A',
    },
    {
      header: 'Automation',
      render: ({ row }) => (
        <InstrumentationChipGroup
          // TODO: Fetch actual coverage from API based on row.address
          coverage={{ lists: 0, alerts: 0, workflows: 0 }}
          showAddChips
          gap="1"
          onListsClick={() => console.log('Add to list:', row.address)}
          onAlertsClick={() => console.log('Create alert:', row.address)}
          onWorkflowsClick={() => console.log('Create workflow:', row.address)}
        />
      ),
    },
    {
      header: 'View',
      render: ({ row }) => <ViewButton to={`/contracts/${row.address}`} />,
    },
  ];

  return (
    <Box>
      <Flex direction="column" gap="4">
        {/* Import Button */}
        <Flex justify="end">
          <Button onClick={() => setImportModalOpen(true)}>
            <PlusIcon /> Import ABI
          </Button>
        </Flex>

        {/* Contracts Table */}
        <GraphQLTable<
          GetContractsQuery,
          GetContractsQueryVariables,
          'contracts',
          ContractNode
        >
          query={GetContractsDocument}
          columns={columns}
          connectionPath="contracts"
          loadingMessage="Fetching contracts..."
          errorMessage="Failed to fetch contracts."
          noDataMessage="No contracts available."
        />
      </Flex>

      {/* Import ABI Modal */}
      <ImportAbiModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        onSuccess={handleImportSuccess}
      />
    </Box>
  );
};

export default ContractList;
